import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '../../common/Drawer';
import SaveCancelButtons from '../../common/buttons/SaveCancelButtons';
import { styled } from 'styled-components';
import { createProject } from '../../../services/project-service';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../data/store';
import { useNavigate } from 'react-router-dom';
import useClientsList from '../../../hooks/useClientsList';
import useUsersList from '../../../hooks/useUsersList';
import { Controller, useForm } from 'react-hook-form';
import {
	asyncCallDone,
	asyncCallStart,
} from '../../../data/stores/projectStore';
import { ProjectDTO } from '../../../data/models/project';
import { showAlert } from '../../../data/stores/alertStore';
import ScreenBusySpinner from '../../common/ScreenBusySpinner';
import Input from '../../common/form/Input';
import Autocomplete from '../../common/form/AutoComplete';
import { AsyncStage } from '../../../data/reduxCommon';
import ProjectCreationConfirmationModal from './ProjectCreationConfirmationModal';
import Link from '../../common/Link';
import { closeModal, showModal } from '../../../data/stores/modalStore';
import { verifyGoogleDriveSetup } from '../../../services/company-service';
import { useTheme, useMediaQuery } from '@mui/material';
import useConfigurationsList from '../../../hooks/useConfigurationsList';
import useProjectSubTypesList from '../../../hooks/useProjecetSubTypesList';
import BusySpinner from '../../common/BusySpinner';

export default function ProjectCreationModal() {
	const {
		openId,
		props: { clientId },
	} = useSelector((state: RootState) => state.modalState);

	const open = openId === 'projectCreationModal';

	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const { stage } = useSelector((state: RootState) => state.projectState);

	const {
		dropdownOptions: clientsDropDownOptions,
		initialized: clientsInitialized,
	} = useClientsList(open);
	const {
		dropdownOptionsFullData: usersDropDownOptions,
		initialized: usersInitialized,
	} = useUsersList(open);
	const {
		dropdownOptionsFullData: configurationsDropDownOptions,
		initialized: configurationsInitialized,
	} = useConfigurationsList(open);
	const {
		dropdownOptionsFullData: projectSubTypesDropDownOptions,
		initialized: projectSubTypesInitialized,
	} = useProjectSubTypesList(open);

	const [modalOpen, setModalOpen] = useState(false);
	const [previewData, setPreviewData] = useState<any>();
	const [googleDriveSetupResponse, setGoogleDriveSetupResponse] =
		useState<any>({ error: false, message: '' });

	const theme = useTheme();
	const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('sm'));

	type FormValues = {
		client: string;
		name: string;
		templateConfiguration: string;
		type: string;
		budgetRange: string;
		workspaceMembers: { id: string; display: string; email: string }[];
		[key: string]: any; // Allow dynamic fields for project subtypes
	};
	const defaultValues = useMemo(() => {
		// Set default workspace members from users with alwaysAddToProjects
		const defaultMembers: { id: string; display: string; email: string }[] =
			[];
		usersDropDownOptions.forEach((user) => {
			if (user.alwaysAddToProjects) {
				defaultMembers.push({
					id: user.id,
					email: user.email,
					display: `${user.firstName} ${user.lastName}`,
				});
			}
		});

		const baseValues: FormValues = {
			client: clientId || '',
			name: '',
			templateConfiguration: '',
			type: '',
			budgetRange: '',
			workspaceMembers: defaultMembers,
		};

		// Default values mapping based on subtype ID
		const getDefaultValueForSubType = (subTypeId: string): string => {
			switch (subTypeId) {
				case 'CAPTURE':
					return 'MOTION_ONLY';
				case 'CREATIVE':
					return 'CREATIVE';
				case 'PLATFORM':
					return 'AGENCY';
				case 'POST':
					return 'MOTION_POST';
				case 'PRODUCTION':
					return 'PRODUCTION';
				default:
					return 'GENERAL';
			}
		};

		// Add default values for each subtype
		projectSubTypesDropDownOptions.forEach((subType) => {
			baseValues[`subType_${subType.id}`] = getDefaultValueForSubType(
				subType.id
			);
		});
		return baseValues;
	}, [clientId, projectSubTypesDropDownOptions, usersDropDownOptions]);

	const { handleSubmit, control, reset, formState, setValue, watch } =
		useForm<FormValues>({
			defaultValues,
			mode: 'onSubmit',
		});

	const { errors } = formState;

	useEffect(() => {
		const handleVerify = async () => {
			const response = await verifyGoogleDriveSetup();
			setGoogleDriveSetupResponse(response);
		};
		if (open) {
			handleVerify();
		}
	}, [open]);

	useEffect(() => {
		// Reset form to default values when modal opens and required data is loaded
		if (open && projectSubTypesInitialized && usersInitialized) {
			reset(defaultValues);
		}
	}, [
		open,
		projectSubTypesInitialized,
		usersInitialized,
		defaultValues,
		reset,
	]);

	const resetForm = () => {
		reset();
	};

	const handlePreview = (data) => {
		const client = clientsDropDownOptions.find(
			(client) => client.id === data.client
		);

		// Collect subtype selections for preview
		const subTypeSelections: { [key: string]: string } = {};
		projectSubTypesDropDownOptions.forEach((subType) => {
			const fieldName = `subType_${subType.id}`;
			if (data[fieldName]) {
				const selectedOption = subType.options.find(
					(option) => option.id === data[fieldName]
				);
				subTypeSelections[subType.display] =
					selectedOption?.display || data[fieldName];
			}
		});

		setPreviewData({
			client: client?.display,
			name: data.name,
			type: data.type,
			budgetRange: data.budgetRange,
			companyTemplateMappingId: data.templateConfiguration,
			workspaceMembers: data.workspaceMembers,
			subTypeSelections,
		});
		setModalOpen(true);
	};

	const handleCreateProject = async (data) => {
		try {
			setModalOpen(false);
			dispatch(asyncCallStart());

			// Collect subtype selections
			const subTypeSelections: { [key: string]: string } = {};
			projectSubTypesDropDownOptions.forEach((subType) => {
				const fieldName = `subType_${subType.id}`;
				if (data[fieldName]) {
					subTypeSelections[subType.id] = data[fieldName];
				}
			});

			const createProjectDTO: ProjectDTO = {
				name: data.name,
				clientId: data.client,
				type: data.type,
				platformType: subTypeSelections.PLATFORM,
				creativeType: subTypeSelections.CREATIVE,
				productionType: subTypeSelections.PRODUCTION,
				captureType: subTypeSelections.CAPTURE,
				postType: subTypeSelections.POST,
				budgetRange: data.budgetRange,
				workspaceMembers: data.workspaceMembers.map(
					(member) => member.id
				),
			};
			const createdProject = await createProject(createProjectDTO);
			navigate(`/projects/${createdProject.id}`);
			handleClose();
			dispatch(
				showAlert({
					message:
						'We recieved your request! Please allow a few minutes up to a few hours for the project to be created.',
					severity: 'success',
				})
			);
		} catch (error) {
			console.log('error', error);
			dispatch(
				showAlert({
					message: 'An error occurred creating the project.',
					severity: 'error',
				})
			);
		} finally {
			dispatch(asyncCallDone());
		}
	};

	const handleCreateNewClient = () => {
		dispatch(showModal({ openId: 'clientCreationModal' }));
	};

	const handleClose = () => {
		dispatch(closeModal());
		resetForm();
		setGoogleDriveSetupResponse({ error: false, message: '' });
	};

	return (
		<>
			<ProjectCreationConfirmationModal
				open={modalOpen}
				handleClose={() => setModalOpen(false)}
				handleSave={handleSubmit(handleCreateProject)}
				previewData={previewData}
			/>
			<Drawer
				open={open}
				handleClose={handleClose}
				title="Create New Project"
				width={600}
			>
				{googleDriveSetupResponse.error && (
					<div>
						<p>{googleDriveSetupResponse.message}</p>
						<MessageNote>
							Note: If changes were recently made to your Google
							permissions, they might take a few minutes to
							propagate.
						</MessageNote>
					</div>
				)}
				{open && !googleDriveSetupResponse.error ? (
					<>
						{!clientsInitialized ||
						!usersInitialized ||
						!configurationsInitialized ||
						!projectSubTypesInitialized ? (
							<BusySpinner display="COMPONENT" />
						) : (
							<>
								<ScreenBusySpinner />
								<FormContainer
									$mobileBreakpoint={mobileBreakpoint}
								>
									<InputContainer>
										<Controller
											name={'name'}
											control={control}
											rules={{
												required: 'Name is required',
											}}
											render={({ field }) => (
												<Input
													{...field}
													label={'Project Name'}
													id="project-name"
													error={!!errors.name}
													helperText={
														errors.name?.message
													}
												/>
											)}
										/>
										<div>
											<Controller
												name={'client'}
												control={control}
												rules={{
													required:
														'Client is required',
												}}
												render={({ field }) => (
													<Autocomplete
														{...field}
														id={'client'}
														label={'Client'}
														options={
															clientsDropDownOptions
														}
														onChange={(
															e,
															value
														) => {
															field.onChange(
																value?.id || ''
															);
														}}
														error={!!errors.client}
														helperText={
															errors.client
																?.message
														}
													/>
												)}
											/>
											<LinkContainer>
												<Link
													onClick={
														handleCreateNewClient
													}
												>
													Don't see your client? Click
													here to add a new client.
												</Link>
											</LinkContainer>
										</div>
										<Controller
											name={'type'}
											control={control}
											rules={{
												required:
													'Project Type is required',
											}}
											render={({ field }) => (
												<Autocomplete
													{...field}
													id={'type'}
													label={'Project Type'}
													options={
														configurationsDropDownOptions
													}
													onChange={(e, value) => {
														field.onChange(
															value?.id || ''
														);
													}}
													error={!!errors.type}
													helperText={
														errors.type?.message
													}
													// disabled={!templateConfiguration}
												/>
											)}
										/>
										{projectSubTypesDropDownOptions.map(
											(subType) => (
												<Controller
													key={subType.id}
													name={`subType_${subType.id}`}
													control={control}
													rules={{
														required: `${subType.display} is required`,
													}}
													render={({ field }) => (
														<Autocomplete
															{...field}
															id={`subType_${subType.id}`}
															label={
																subType.display
															}
															options={subType.options.map(
																(option) => ({
																	id: option.id,
																	display:
																		option.display,
																})
															)}
															onChange={(
																e,
																value
															) => {
																field.onChange(
																	value?.id ||
																		''
																);
															}}
															error={
																!!errors[
																	`subType_${subType.id}`
																]
															}
															helperText={
																typeof errors[
																	`subType_${subType.id}`
																]?.message ===
																'string'
																	? (errors[
																			`subType_${subType.id}`
																	  ]
																			?.message as string)
																	: undefined
															}
														/>
													)}
												/>
											)
										)}
										<Controller
											name={'workspaceMembers'}
											control={control}
											rules={{
												required:
													'Members are required',
											}}
											render={({ field }) => (
												<Autocomplete
													{...field}
													id={'workspaceMembers'}
													label={'Workspace Members'}
													options={
														usersDropDownOptions
													}
													onChange={(e, value) => {
														field.onChange(value);
													}}
													error={
														!!errors.workspaceMembers
													}
													helperText={
														errors.workspaceMembers
															?.message
													}
													multiple
												/>
											)}
										/>
										<br />
										<SaveCancelButtons
											handleSave={handleSubmit(
												handlePreview
											)}
											handleCancel={handleClose}
											saveText={'Review'}
											saveDisabled={
												stage === AsyncStage.Busy
											}
											cancelDisabled={
												stage === AsyncStage.Busy
											}
										/>
									</InputContainer>
								</FormContainer>
							</>
						)}
					</>
				) : (
					<></>
				)}
			</Drawer>
		</>
	);
}

const LinkContainer = styled.div`
	font-size: 12px;
	text-align: left;
	margin-top: 5px;
`;

const FormContainer = styled.div<{ $mobileBreakpoint: boolean }>`
	width: 100%;
`;

const InputContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(2)};
`;

const MessageNote = styled.div`
	font-size: 12px;
	text-align: left;
	margin-top: 5px;
	color: ${({ theme }) => theme.palette.text.secondary};
`;
